import * as React from 'react';
import { IDepot, IBac, IPlanningEntry, ICapacite } from '../../models';
import { useData } from '../../core/context/ProvidersContext';
import { useBacActions } from '../../hooks/useBacActions';
import { StatusBadge } from '../shared/StatusBadge';
import { getContentColor } from '../shared/ProductColors';
import { generateMonthRange, formatMonthLabel, isCurrentMonth } from '../shared/MonthUtils';
import { BacEditorModal } from './BacEditorModal';
import { BacActionHistoryModal } from '../actions/BacActionHistoryModal';
import styles from './DepotsView.module.scss';

export interface IDepotDetailProps {
  depot: IDepot;
  bacs: IBac[];
  planning: IPlanningEntry[];
  capacites: ICapacite[];
  isAdmin: boolean;
  onBack: () => void;
  onChanged: () => void;
  kpi: {
    bacsCount: number;
    volumeTotal: number;
    indispoCount: number;
    currentProducts: string[];
    withRestriction: number;
  };
}

export const DepotDetail: React.FC<IDepotDetailProps> = ({
  depot, bacs, planning, capacites, isAdmin, onBack, onChanged, kpi,
}) => {
  const data = useData();
  const [editingBac, setEditingBac] = React.useState<IBac | null>(null);
  const [historyBac, setHistoryBac] = React.useState<IBac | null>(null);
  const [editingName, setEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState(depot.Title);
  const [savingName, setSavingName] = React.useState(false);

  const bacIds = React.useMemo(() => bacs.map(b => b.Id), [bacs]);
  const { actions: allBacActions, refresh: refreshBacActions } = useBacActions(
    bacIds.length > 0 ? { bacIds } : undefined
  );

  const actionsByBacId = React.useMemo(() => {
    const map: Record<number, typeof allBacActions> = {};
    for (const a of allBacActions) {
      if (!map[a.BacId]) map[a.BacId] = [];
      map[a.BacId].push(a);
    }
    return map;
  }, [allBacActions]);

  // Timeline: 12 months starting from the current month.
  const months = React.useMemo(() => {
    const now = new Date();
    return generateMonthRange(now.getFullYear(), now.getMonth() + 1, 12);
  }, []);

  const entryMap = React.useMemo(() => {
    const map: Record<string, IPlanningEntry> = {};
    planning.forEach(e => {
      const key = `${e.BacId}-${e.Mois.substring(0, 7)}`;
      map[key] = e;
    });
    return map;
  }, [planning]);

  const sortedBacs = React.useMemo(
    () => [...bacs].sort((a, b) => a.Title.localeCompare(b.Title, 'fr', { numeric: true })),
    [bacs]
  );

  const allocationStats = React.useMemo(() => {
    const bacById = new Map(bacs.map(b => [b.Id, b]));
    let totalAlloue = 0;
    let totalRestrictionAmount = 0;
    for (const c of capacites) {
      totalAlloue += c.VolumeAlloue;
      const bac = bacById.get(c.BacId);
      if (bac && bac.RestrictionClients) {
        totalRestrictionAmount += c.VolumeAlloue * Math.abs(bac.RestrictionClients);
      }
    }
    return {
      totalAlloue,
      totalRestrictionAmount,
      totalAvailable: Math.max(0, totalAlloue - totalRestrictionAmount),
      restrictionPct: totalAlloue > 0 ? totalRestrictionAmount / totalAlloue : 0,
    };
  }, [capacites, bacs]);

  const fmtInt = (n: number): string => Math.round(n).toLocaleString('fr-FR');

  const handleRenameSave = async (): Promise<void> => {
    if (!nameDraft.trim() || nameDraft === depot.Title) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await data.updateDepot(depot.Id, nameDraft.trim());
      setEditingName(false);
      onChanged();
    } catch (e) {
      alert('Impossible de renommer : ' + (e as Error).message);
    } finally {
      setSavingName(false);
    }
  };

  const handleBacSave = async (updated: Partial<IBac>): Promise<void> => {
    if (!editingBac) return;
    await data.updateBac(editingBac.Id, updated);
    setEditingBac(null);
    onChanged();
  };

  const handleBacDelete = async (bac: IBac): Promise<void> => {
    if (!confirm(`Supprimer le bac "${bac.Title}" ? Cette action est irréversible.`)) return;
    await data.deleteBac(bac.Id);
    onChanged();
  };

  const handleHistoryDeleted = (): void => {
    refreshBacActions();
    onChanged();
  };

  return (
    <div className={styles.detailWrapper}>
      <div className={styles.detailHeader}>
        <button className={styles.backBtn} onClick={onBack}>
          <span className={styles.backArrow}>←</span> Tous les dépôts
        </button>

        <div className={styles.detailTitleRow}>
          {editingName && isAdmin ? (
            <div className={styles.renameRow}>
              <input
                className={styles.renameInput}
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRenameSave(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus
              />
              <button className={styles.btnPrimary} onClick={handleRenameSave} disabled={savingName}>
                {savingName ? '…' : 'OK'}
              </button>
              <button className={styles.btnGhost} onClick={() => { setEditingName(false); setNameDraft(depot.Title); }}>
                Annuler
              </button>
            </div>
          ) : (
            <>
              <h1 className={styles.detailTitle}>{depot.Title}</h1>
              {isAdmin && (
                <button
                  className={styles.editIconBtn}
                  title="Renommer le dépôt"
                  onClick={() => setEditingName(true)}
                >
                  ✎
                </button>
              )}
            </>
          )}
        </div>

        <div className={styles.detailKpiRow}>
          <div className={styles.detailKpi}>
            <div className={styles.detailKpiValue}>{kpi.bacsCount}</div>
            <div className={styles.detailKpiLabel}>bacs</div>
          </div>
          <div className={styles.detailKpi}>
            <div className={styles.detailKpiValue}>{kpi.volumeTotal.toLocaleString('fr-FR')}<span className={styles.detailKpiUnit}> m³</span></div>
            <div className={styles.detailKpiLabel}>capacité totale</div>
          </div>
          <div className={styles.detailKpi}>
            <div className={styles.detailKpiValue}>{kpi.indispoCount}</div>
            <div className={styles.detailKpiLabel}>indispo ce mois</div>
          </div>
          <div className={styles.detailKpi}>
            <div className={styles.detailKpiValue}>{kpi.withRestriction}</div>
            <div className={styles.detailKpiLabel}>sous restriction</div>
          </div>
        </div>

        <div className={styles.allocationStrip}>
          <div className={styles.allocKpi}>
            <div className={styles.allocValue}>{fmtInt(allocationStats.totalAlloue)}<span className={styles.allocUnit}> m³</span></div>
            <div className={styles.allocLabel}>Total alloué</div>
          </div>
          <div className={styles.allocArrow}>−</div>
          <div className={`${styles.allocKpi} ${styles.allocKpiRestriction}`}>
            <div className={styles.allocValue}>
              {allocationStats.totalRestrictionAmount > 0 ? '−' : ''}{fmtInt(allocationStats.totalRestrictionAmount)}
              <span className={styles.allocUnit}> m³</span>
            </div>
            <div className={styles.allocLabel}>
              Restrictions
              {allocationStats.restrictionPct > 0 && (
                <span className={styles.allocSubValue}>
                  {' '}({Math.round(allocationStats.restrictionPct * 100)}%)
                </span>
              )}
            </div>
          </div>
          <div className={styles.allocArrow}>=</div>
          <div className={`${styles.allocKpi} ${styles.allocKpiAvailable}`}>
            <div className={styles.allocValue}>{fmtInt(allocationStats.totalAvailable)}<span className={styles.allocUnit}> m³</span></div>
            <div className={styles.allocLabel}>Alloué disponible</div>
          </div>
        </div>

        {kpi.currentProducts.length > 0 && (
          <div className={styles.detailProducts}>
            <span className={styles.detailProductsLabel}>Produits stockés ce mois :</span>
            {kpi.currentProducts.map(p => (
              <StatusBadge key={p} contenu={p} compact />
            ))}
          </div>
        )}
      </div>

      <div className={styles.detailBody}>
        <div className={styles.detailSectionTitle}>
          <h3>Bacs du dépôt</h3>
          <span className={styles.muted}>{sortedBacs.length} enregistrements</span>
        </div>

        <div className={styles.bacsTableWrapper}>
          <table className={styles.bacsTable}>
            <thead>
              <tr>
                <th>Bac</th>
                <th>Volume</th>
                <th>Restriction</th>
                <th>Pt dépôt</th>
                <th>Info clients</th>
                <th>Commentaire</th>
                <th className={styles.timelineHeader} colSpan={12}>
                  Planning 12 prochains mois
                </th>
                <th>Historique</th>
                {isAdmin && <th className={styles.stickyRight}>Actions</th>}
              </tr>
              <tr className={styles.monthsSubrow}>
                <th colSpan={6}></th>
                {months.map(m => (
                  <th
                    key={m}
                    className={isCurrentMonth(m) ? styles.currentMonthHeader : undefined}
                  >
                    {formatMonthLabel(m)}
                  </th>
                ))}
                <th></th>
                {isAdmin && <th className={styles.stickyRight}></th>}
              </tr>
            </thead>
            <tbody>
              {sortedBacs.map((bac, idx) => {
                const restriction = bac.RestrictionClients
                  ? `${Math.round(bac.RestrictionClients * 100)}%`
                  : '';
                return (
                  <tr
                    key={bac.Id}
                    className={styles.bacRow}
                    style={{ animationDelay: `${idx * 0.025}s` }}
                  >
                    <td className={styles.bacTitle}>{bac.Title}</td>
                    <td className={styles.numericCell}>
                      {bac.VolumeUtile?.toLocaleString('fr-FR') || ''}
                    </td>
                    <td className={bac.RestrictionClients && bac.RestrictionClients < 0 ? styles.restrictionNegative : ''}>
                      {restriction}
                    </td>
                    <td>
                      <span className={bac.PointDepot ? styles.yes : styles.no}>
                        {bac.PointDepot ? '●' : '○'}
                      </span>
                    </td>
                    <td>
                      <span className={bac.InfoClients ? styles.yes : styles.no}>
                        {bac.InfoClients ? '●' : '○'}
                      </span>
                    </td>
                    <td className={styles.commentCell} title={bac.Commentaire}>
                      {bac.Commentaire || <span className={styles.muted}>—</span>}
                    </td>
                    {months.map(m => {
                      const key = `${bac.Id}-${m.substring(0, 7)}`;
                      const entry = entryMap[key];
                      const color = entry ? getContentColor(entry.Contenu) : null;
                      return (
                        <td
                          key={m}
                          className={`${styles.timelineCell} ${isCurrentMonth(m) ? styles.currentMonthCell : ''}`}
                          title={entry ? entry.Contenu : ''}
                          style={color ? { background: color.background, color: color.text } : undefined}
                        >
                          {entry && color ? color.label || entry.Contenu : ''}
                        </td>
                      );
                    })}
                    <td className={styles.historyCell}>
                      {(() => {
                        const cnt = (actionsByBacId[bac.Id] || []).length;
                        return (
                          <button
                            className={cnt > 0 ? styles.historyBtn : styles.historyBtnEmpty}
                            onClick={() => setHistoryBac(bac)}
                            title={cnt > 0 ? `${cnt} action(s) enregistrée(s)` : 'Aucun historique'}
                          >
                            <span className={styles.historyBtnIcon}>📚</span>
                            {cnt > 0 && <span className={styles.historyBtnCount}>{cnt}</span>}
                          </button>
                        );
                      })()}
                    </td>
                    {isAdmin && (
                      <td className={styles.stickyRight}>
                        <button
                          className={styles.btnSmall}
                          onClick={() => setEditingBac(bac)}
                        >
                          ✎ Modifier
                        </button>
                        <button
                          className={styles.btnSmallDanger}
                          onClick={() => handleBacDelete(bac)}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editingBac && (
        <BacEditorModal
          bac={editingBac}
          onSave={handleBacSave}
          onCancel={() => setEditingBac(null)}
        />
      )}

      {historyBac && (
        <BacActionHistoryModal
          bac={historyBac}
          actions={actionsByBacId[historyBac.Id] || []}
          isAdmin={isAdmin}
          onClose={() => setHistoryBac(null)}
          onDeleted={handleHistoryDeleted}
        />
      )}
    </div>
  );
};
